import React, { useState, useEffect } from "react";
import patientService from "../services/patientService";
import { toast } from "react-toastify";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

const EditPatientForm = ({
  selectedPatient,
  setSelectedPatient,
  isEditing,
  setIsEditing,
}) => {
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedPatient) {
      const { id, ...patientData } = selectedPatient;
      setFormData({
        ...patientData,
        age: calculateAge(selectedPatient.birthdate),
      });
      setOriginalData(patientData);
    }
  }, [selectedPatient]);

  const calculateAge = (birthdate) => {
    if (!birthdate) return "No disponible";
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
      case "surname":
      case "country":
      case "city":
        if (!/^[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+$/.test(value)) {
          error = "Este campo solo puede contener letras y espacios.";
        }
        break;
      case "document":
        if (!/^\d{6,10}$/.test(value)) {
          error = "El documento debe tener entre 6 y 10 dígitos.";
        }
        break;
      case "email":
        if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)) {
          error = "El formato del correo electrónico no es válido.";
        }
        break;
      case "phone":
        if (!/^\+?\d+$/.test(value)) {
          error =
            "El número de teléfono solo puede contener dígitos y, opcionalmente, el símbolo '+' al inicio.";
        }
        break;
      case "gender":
        if (!["Masculino", "Femenino", "Otro"].includes(value)) {
          error = "Ingresa una opción válida: Masculino, Femenino u Otro.";
        }
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    return error === "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "birthdate" && { age: calculateAge(value) }),
    }));
  };

  const handleSubmit = async () => {
    let isValid = true;
    Object.keys(formData).forEach((key) => {
      if (!validateField(key, formData[key])) {
        isValid = false;
      }
    });

    if (!isValid) {
      toast.error(
        "Revisa los campos y corrige cualquier error antes de guardar."
      );
      return;
    }

    try {
      const db = getFirestore();
      const patientsCollection = collection(db, "registeredPatients");

      const q = query(
        patientsCollection,
        where("document", "==", formData.document),
        where("group", "==", selectedPatient.group)
      );
      const querySnapshot = await getDocs(q);

      const existingPatients = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((patient) => patient.id !== selectedPatient.id);

      if (existingPatients.length > 0) {
        toast.error("Ya existe un paciente con este documento en este grupo.");
        return;
      }

      await patientService.updatePatientData(selectedPatient.id, formData);

      toast.success("La información se guardó correctamente.");
      setIsEditing(false);
    } catch (error) {
      toast.error("Ocurrió un error al actualizar la información.");
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
  };

  const orderedFields = [
    { key: "name", label: "Nombre" },
    { key: "surname", label: "Apellido" },
    { key: "document", label: "Documento" },
    { key: "email", label: "Correo electrónico" },
    { key: "phone", label: "Teléfono" },
    { key: "birthdate", label: "Fecha de nacimiento" },
    { key: "age", label: "Edad" },
    { key: "gender", label: "Género" },
    { key: "country", label: "País" },
    { key: "city", label: "Ciudad" },
  ];

  return (
    <div>
      {orderedFields.map(({ key, label }) => (
        <div key={key}>
          <label className="generalFormLabel">{label}</label>
          <input
            type={key === "birthdate" ? "date" : "text"}
            name={key}
            value={formData[key] || ""}
            onChange={handleChange}
            readOnly={!isEditing || key === "age"}
            className="generalFormInput"
          />
          {errors[key] && <p style={{ color: "red" }}>{errors[key]}</p>}
        </div>
      ))}
      {isEditing && (
        <div className="butttonContainer">
          <button type="button" onClick={handleSubmit} className="smallButton">
            Guardar
          </button>
          <button type="button" onClick={handleCancel} className="smallButton">
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default EditPatientForm;
